import {Menu, MenuItem, Typography} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import {useEffect, ReactNode, useRef, useCallback} from 'react';

interface AurMenuProps {
  menuItems: AurMenuItem[];
  direction?: 'left' | 'right';
  cssClassName?: string;
  labelClassName?: string;
  anchorElement: null | HTMLElement | undefined;
  shouldShow: boolean;
  onClose: () => void;
}

export type AurMenuItem = {
  label: string;
  onClick: () => void;
  cssClassName?: string;
  tooltip?: string;
  selected?: boolean;
  children?: ReactNode;
};

const AurMenu = (props: AurMenuProps) => {
  const {labelClassName, shouldShow, onClose} = props;
  const menuRef = useRef<HTMLDivElement | null>(null);

  const tooltipClasses = {
    arrow: 'aur-menu_tooltip-arrow',
    tooltip: 'aur-menu_tooltip-tooltip',
  };

  const handleCloseOnScroll = useCallback(
    (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (menuRef.current && target && menuRef.current.contains(target)) {
        return;
      }
      onClose();
    },
    [onClose]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if(e.key === 'Escape'){
      e.preventDefault();
      onClose();
    }
  }

  useEffect(() => {
    if (!shouldShow) return;

    window.addEventListener('scroll', handleCloseOnScroll, true);
    window.addEventListener('resize', handleCloseOnScroll);

    return () => {
      window.removeEventListener('scroll', handleCloseOnScroll, true);
      window.removeEventListener('resize', handleCloseOnScroll);
    };
  }, [shouldShow, handleCloseOnScroll]);

  return (
    <Menu
      ref={menuRef}
      className={
        props.cssClassName ? props.cssClassName + ' aur-menu' : 'aur-menu'
      }
      anchorEl={props.anchorElement}
      onClose={onClose}
      open={shouldShow}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: props.direction || 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: props.direction || 'left',
      }}
      disableScrollLock
      TransitionProps={{timeout: 0}}
      onKeyDown={(event : React.KeyboardEvent<HTMLDivElement>) => handleKeyDown(event)}
    >
      {props.menuItems.map((item, index) => (
        <Tooltip
          title={item.tooltip || ''}
          key={index}
          placement="right-start"
          arrow
          disableInteractive
          classes={tooltipClasses}
          slotProps={{
            popper: {
              modifiers: [{name: 'offset', options: {offset: [8, 10]}}],
            },
          }}
        >
          <MenuItem
            className={
              item.cssClassName
                ? item.cssClassName + ' aur-menu-item'
                : 'aur-menu-item'
            }
            selected={item?.selected}
            onClick={() => {
              item.onClick();
              onClose();
            }}
            aria-live="polite"
            role="menuItem"
          >
            <Typography
              className={
                labelClassName
                  ? `aur-menu-item-label ${labelClassName}`
                  : 'typo-text body3_size aur-menu-item-label'
              }
            >
              {item.label}
            </Typography>
            {item.selected && item.children ? item.children : null}
          </MenuItem>
        </Tooltip>
      ))}
    </Menu>
  );
};

export default AurMenu;
